/**
 * F6 · Chats (slice 1 spec §7.1): the threads I started, a new-thread
 * composer, and the open thread (`/chats/:threadId`) on the right. The
 * Everyone tab is parked until workspaces have more people (slice 6).
 */
import { Button, buttonVariants } from "@crew/ui/components/button";
import { ChatIcon } from "@crew/ui/components/chat-icon";
import { AgentAvatar, AvatarRow, PersonAvatar } from "@crew/ui/components/crew-avatar";
import { FULL_HEIGHT_PAGE } from "@crew/ui/components/family-shell";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@crew/ui/components/drawer";
import { Input } from "@crew/ui/components/input";
import { Item, ItemContent, ItemFooter, ItemMedia } from "@crew/ui/components/item";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { StatusBadge } from "@crew/ui/components/status";
import { PageTitle } from "@crew/ui/components/typography";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { CheckCheck, ChevronLeft, Ellipsis, Pencil } from "lucide-react";
import { Fragment, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { errorHint } from "../../app/api";
import { useMe, type Me } from "../../app/me";
import {
  useMoveThread,
  useRenameThread,
  useThread,
  useThreads,
  type ThreadDetail,
  type ThreadItem,
} from "./api";
import { chiefOf, starterOf } from "./authors";
import { replyCount, replyCountLabel, shortTime } from "./format";
import { useHeldAtBottom } from "./list-scroll";
import { SettleWindow } from "./settle";
import { StartComposer } from "./start-composer";
import { startDrafts } from "./starts";
import { threadsFor } from "./tabs";
import { ThreadView } from "./thread-view";
import { ThreadTasksChip } from "../tasks/parts";
import { threadTasksHref, threadTasksLabel } from "../tasks/task-view";
import { MessagingMark } from "../messaging/parts";
import { messagingHeader } from "../messaging/view";
import { AreaFilter, AreaPicker } from "../areas/area-controls";
import { useAreaFilter } from "../areas/use-area-filter";
import { filingArea, movesLine, openTasksMoving } from "../areas/area-view";
import { AreaChip } from "../areas/parts";
import type { AreaRef } from "../areas/api";

/** The list's small badge from `activity` (spec §7.3). */
export function ActivityBadge({ activity }: { activity: ThreadItem["activity"] }) {
  if (activity === "running") return <StatusBadge tone="done">Working</StatusBadge>;
  if (activity === "queued") return <StatusBadge tone="working">Queued</StatusBadge>;
  // Slice 6c §7.1: a handoff in it waits on someone.
  if (activity === "handoff") return <StatusBadge tone="waiting">Waiting on you</StatusBadge>;
  return null;
}

/** The avatar of whoever started a thread. */
function StartedBy({ thread, size }: { thread: ThreadItem; size: number }) {
  const face = starterOf(thread);
  return <PersonAvatar initials={face.initials} tint={face.tint} size={size} />;
}

function ChiefAvatar({ me }: { me: Me }) {
  const chief = chiefOf(me);
  if (chief.kind !== "agent") return null;
  return (
    <AvatarRow>
      <AgentAvatar glyph={chief.glyph} tint={chief.tint} size={18} />
    </AvatarRow>
  );
}

function RowFooter({ thread, me }: { thread: ThreadItem; me: Me }) {
  return (
    <ItemFooter className="mt-1 justify-start gap-2">
      {thread.reply_count > 0 ? <ChiefAvatar me={me} /> : null}
      <span className="text-sm font-semibold text-foreground">
        {replyCountLabel(thread.reply_count)}
      </span>
      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
        last {shortTime(thread.last_message_at, me.workspace.timezone)}
      </span>
      {/* Slice 4 §6.4: settled until a person writes again. */}
      {thread.settled ? (
        <span className="text-xs text-muted-foreground" data-settled>
          Settled
        </span>
      ) : null}
      <ActivityBadge activity={thread.activity} />
    </ItemFooter>
  );
}

function ThreadRow({
  thread,
  me,
  current,
  search,
}: {
  thread: ThreadItem;
  me: Me;
  current: boolean;
  /** The list's filter, kept as a thread opens. */
  search: string;
}) {
  return (
    <Item
      variant={current ? "muted" : "default"}
      render={
        <Link href={`/chats/${thread.id}${search}`} aria-current={current ? "page" : undefined} />
      }
      className="flex-nowrap items-start gap-3 px-3 py-3"
      data-thread-id={thread.id}
      data-row-key={thread.id}
      data-row-stamp={thread.last_message_at}
    >
      <ItemMedia>
        <StartedBy thread={thread} size={28} />
      </ItemMedia>
      <ItemContent className="min-w-0 gap-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-foreground">{starterOf(thread).name}</span>
          <span className="text-xs text-muted-foreground">
            {shortTime(thread.created_at, me.workspace.timezone)}
          </span>
        </div>
        <p className="line-clamp-2 text-sm text-foreground">
          {thread.messaging_app ? (
            // Slice 2e (spec §6.3): the person's iMessage thread; slice 2h, an email chat.
            <span className="mr-1.5 inline-block align-[-2px]">
              <MessagingMark app={thread.messaging_app} size={13} />
            </span>
          ) : null}
          {thread.title}
          {/* Slice 6d (spec §4.4): its Area, after the title. */}
          <AreaChip area={thread.area} className="ml-1.5" />
        </p>
        <RowFooter thread={thread} me={me} />
      </ItemContent>
    </Item>
  );
}

/**
 * At the top of the list: the next older page loads as it comes into view
 * (a little before), or on a click. The list holds the rows in view still.
 * A page that failed waits for the click: loading it again on sight would
 * retry in a loop while the button stays in view.
 */
function OlderThreads({ threads }: { threads: ReturnType<typeof useThreads> }) {
  const ref = useRef<HTMLButtonElement>(null);
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = threads;
  const pages = threads.data?.pages.length ?? 0;
  // Observed again after each page, so a page too short to scroll loads the next.
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    const seen = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void fetchNextPage();
      },
      { root: el.closest("[data-thread-list]"), rootMargin: "300px 0px 0px 0px" },
    );
    seen.observe(el);
    return () => seen.disconnect();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage, pages]);
  if (!hasNextPage) return null;
  return (
    <div className="flex flex-col items-center gap-1">
      <Button
        ref={ref}
        variant="ghost"
        disabled={isFetchingNextPage}
        onClick={() => void fetchNextPage()}
        data-older-threads
      >
        {isFetchingNextPage ? <Spinner /> : null}
        Older threads
      </Button>
      {isFetchNextPageError && !isFetchingNextPage ? (
        <p className="px-3 text-center text-meta text-muted-foreground" data-older-threads-error>
          {errorHint(threads.error)}
        </p>
      ) : null}
    </div>
  );
}

function ThreadRows({
  threads,
  me,
  currentId,
  filtered,
}: {
  threads: ReturnType<typeof useThreads>;
  me: Me;
  currentId?: string;
  filtered: boolean;
}) {
  const { search } = useLocation();
  // An older page or a refetch that failed keeps the rows already loaded.
  if (!threads.data) {
    if (threads.error) {
      return (
        <p className="px-3 py-6 text-body text-muted-foreground">{errorHint(threads.error)}</p>
      );
    }
    return (
      <div className="flex justify-center py-10 text-muted-foreground">
        <Spinner className="size-5" />
      </div>
    );
  }
  const { shown, empty } = threadsFor(threads.data.pages, "mine");
  return (
    <>
      <OlderThreads threads={threads} />
      {shown.map((t) => (
        <ThreadRow key={t.id} thread={t} me={me} current={t.id === currentId} search={search} />
      ))}
      {empty ? (
        <p className="px-3 py-6 text-body text-muted-foreground">
          {filtered ? "No chats here yet." : empty}
        </p>
      ) : null}
    </>
  );
}

/**
 * The threads I started, oldest at the top and the newest at the bottom, just
 * above the composer, like a channel. It opens at the bottom, and older pages
 * load as you scroll up (`list-scroll.ts`).
 */
export function ThreadList({
  me,
  currentId,
  area = null,
}: {
  me: Me;
  currentId?: string;
  /** The list's Area filter (slice 6d spec §4.4): a slug, `none`, or all. */
  area?: string | null;
}) {
  const threads = useThreads("mine", area);
  const { ref, onScroll } = useHeldAtBottom<HTMLDivElement>();
  return (
    <div
      ref={ref}
      onScroll={onScroll}
      data-thread-list
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pt-2 [overflow-anchor:none] md:pt-0"
    >
      {/* Short of the scroller's height, the rows sit at its bottom, by the composer. */}
      <div className="mt-auto flex flex-col gap-1">
        <ThreadRows threads={threads} me={me} currentId={currentId} filtered={area !== null} />
      </div>
    </div>
  );
}

function TitleEditor({
  threadId,
  title,
  onDone,
}: {
  threadId: string;
  title: string;
  onDone: () => void;
}) {
  const rename = useRenameThread(threadId);
  const [draft, setDraft] = useState(title);
  const save = (event: FormEvent) => {
    event.preventDefault();
    rename.mutate(draft, { onSuccess: onDone });
  };
  return (
    <form onSubmit={save} className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex items-center gap-2">
        <Input
          aria-label="Thread title"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && onDone()}
          maxLength={80}
          autoFocus
          className="h-8 flex-1"
        />
        <Button type="submit" size="sm" disabled={rename.isPending}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {rename.error ? (
        <span className="text-meta text-brick">{errorHint(rename.error)}</span>
      ) : null}
    </form>
  );
}

/**
 * The chat's Area (slice 6d spec §8.3; A2b, A2c): its chip, or "Add to an
 * area", which opens the Area sheet. Moving it says how many of its open
 * tasks follow. A messaging thread has none, and no control.
 */
function ThreadArea({
  detail,
  me,
  className = "-ml-2 shrink-0",
}: {
  detail: ThreadDetail;
  me: Me;
  className?: string;
}) {
  const move = useMoveThread(detail.thread.id);
  // An email chat mixes Areas; a texted chat is about one thing (slice 2i spec §7).
  if (detail.thread.messaging_app === "email") return null;
  const current = detail.thread.area ?? null;
  return (
    <>
      <AreaPicker
        me={me}
        current={current}
        title="Which area is this chat in?"
        line={movesLine(openTasksMoving(detail.tasks ?? [], current?.id ?? null))}
        onPick={(areaId) => move.mutate(areaId)}
        busy={move.isPending}
        error={move.error}
        newArea
        className={className}
      />
      <FiledByCrew thread={detail.thread} />
    </>
  );
}

/** Spec §7.1 (A2b): a chat Clef filed says so beside its chip, which is its Change. */
function FiledByCrew({ thread }: { thread: ThreadDetail["thread"] }) {
  if (!thread.area || thread.area_filed_by !== "clef") return null;
  return (
    <span data-filed-by-crew className="shrink-0 text-meta text-muted-foreground">
      Filed by the crew
    </span>
  );
}

/** Under the title, not on a phone: its Area, then who it's with and its replies, or how it's texted. */
function ThreadLine({
  area,
  line,
  detail,
}: {
  area: ReactNode;
  line: string | null;
  detail: ThreadDetail;
}) {
  return (
    <div className="hidden min-w-0 items-center gap-1 text-meta text-muted-foreground md:flex">
      {area}
      {area ? <span aria-hidden>·</span> : null}
      <span className="truncate" data-thread-line>
        {line ?? withLine(detail)}
      </span>
    </div>
  );
}

/** Who a chat is with and how many replies it has. */
const withLine = (detail: ThreadDetail) =>
  `Chief of Staff · ${replyCountLabel(replyCount(detail.messages))}`;

/**
 * A phone's filed chat (Paper HF): its Area on a small tab hanging off the
 * header's bottom edge, so the header stays one line. It opens Chat info.
 */
function AreaTab({ area, onOpen }: { area: AreaRef; onOpen: () => void }) {
  return (
    <button
      type="button"
      data-area-tab={area.slug}
      aria-label={`Area: ${area.label}. Chat info`}
      onClick={onOpen}
      className={cn(
        "absolute top-full right-4 z-10 flex h-[26px] max-w-[60%] items-center gap-1.5 rounded-b-lg border border-t-0 border-border bg-sidebar px-2.5 text-xs font-medium shadow-[0_2px_4px_rgb(31_42_68/6%)] outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden",
        // 44px to tap, though it's 26px tall.
        "after:absolute after:-inset-x-1 after:-inset-y-[9px]",
        area.archived ? "text-hint" : "text-moss",
      )}
    >
      <ChatIcon name={area.icon} size={12} />
      <span className="truncate">{area.label}</span>
    </button>
  );
}

/**
 * A phone's Chat info (Paper HF): the title and Rename, the chat's Area (its
 * chip or "Add to an area", opening the Area sheet over it), its tasks, and
 * who it's with. An email chat has no Area row.
 */
function ChatInfo({
  detail,
  me,
  line,
  open,
  onOpenChange,
  onRename,
}: {
  detail: ThreadDetail;
  me: Me;
  line: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRename: () => void;
}) {
  const tasks = threadTasksLabel(detail.tasks);
  const [settling, setSettling] = useState(false);
  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
        <DrawerContent className="pb-[env(safe-area-inset-bottom)]" data-chat-info>
          <DrawerHeader className="flex-row items-start gap-3 pb-4 text-left group-data-[swipe-axis=y]/drawer-popup:text-left">
            <DrawerTitle className="min-w-0 flex-1 text-lead leading-6 font-semibold">
              {detail.thread.title}
            </DrawerTitle>
            <Button
              variant="link"
              className="h-6 shrink-0 px-0 text-copy underline"
              onClick={() => {
                onOpenChange(false);
                onRename();
              }}
            >
              Rename
            </Button>
          </DrawerHeader>
          <dl className="mx-4 mb-4 flex flex-col divide-y divide-border rounded-xl bg-card text-body ring-1 ring-border">
            {detail.thread.messaging_app === "email" ? null : (
              <InfoRow label="Area">
                <ThreadArea detail={detail} me={me} className="-ml-2 min-w-0" />
              </InfoRow>
            )}
            <InfoRow label="Tasks">
              {tasks ? (
                <Link href={threadTasksHref(detail.tasks)} className="truncate text-copy">
                  {tasks}
                </Link>
              ) : (
                <span className="text-muted-foreground">
                  {detail.tasks?.length ? "No open tasks" : "None yet"}
                </span>
              )}
            </InfoRow>
            <InfoRow label="With">
              <span className="truncate text-copy">{line ?? withLine(detail)}</span>
            </InfoRow>
          </dl>
          {/* Slice 4 §6.3: Settle, once it's done with; "Settled" until a person writes again. */}
          {detail.thread.settled ? (
            <p className="mx-4 mb-4 text-meta text-muted-foreground">Settled</p>
          ) : (
            <Button
              variant="outline"
              className="mx-4 mb-4"
              onClick={() => {
                onOpenChange(false);
                setSettling(true);
              }}
            >
              <CheckCheck />
              Settle this chat
            </Button>
          )}
        </DrawerContent>
      </Drawer>
      <SettleWindow threadId={detail.thread.id} open={settling} onOpenChange={setSettling} />
    </>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-12 items-center gap-3 px-4 py-2">
      <dt className="w-14 shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">{children}</dd>
    </div>
  );
}

/**
 * The open thread's title, which anyone in the workspace can rename (once it's
 * started). A messaging thread carries its mark and whether replies are texted
 * (slice 2e spec §6.3), and Link again once its link is gone. On a phone the
 * header is one line (Paper HF): the title and "…" open Chat info, and a filed
 * chat's Area hangs below it on a tab.
 */
function ThreadTitle({
  detail,
  me,
  onRename,
}: {
  detail: ThreadDetail;
  me: Me;
  onRename?: () => void;
}) {
  const [info, setInfo] = useState(false);
  const texting = messagingHeader(detail, me);
  const line = texting?.line ?? null;
  // Its Area once it's started (a start's placeholder has nothing to move yet).
  const filable = Boolean(onRename) && detail.thread.messaging_app !== "email";
  return (
    <>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <TitleHeading detail={detail} texting={Boolean(texting)} onOpen={onRename && setInfo} />
        <ThreadLine
          area={filable && <ThreadArea detail={detail} me={me} />}
          line={line}
          detail={detail}
        />
      </div>
      {texting?.linkAgain && (
        <Link
          href="/settings/messaging"
          className={buttonVariants({ variant: "outline", size: "sm" })}
        >
          Link again
        </Link>
      )}
      <HeaderTasks detail={detail} started={Boolean(onRename)} />
      {onRename && (
        <TitleControls
          detail={detail}
          me={me}
          line={line}
          filable={filable}
          info={info}
          onInfo={setInfo}
          onRename={onRename}
        />
      )}
    </>
  );
}

/** The chat's tasks chip; on a phone, once started, they're in Chat info, so the title keeps the room. */
function HeaderTasks({ detail, started }: { detail: ThreadDetail; started: boolean }) {
  return (
    <div className={started ? "hidden min-w-0 md:contents" : "contents"}>
      <ThreadTasksChip tasks={detail.tasks} />
    </div>
  );
}

/** The title; on a phone a button that opens Chat info (Paper HF). */
function TitleHeading({
  detail,
  texting,
  onOpen,
}: {
  detail: ThreadDetail;
  texting: boolean;
  onOpen?: (open: true) => void;
}) {
  const phone = useIsMobile();
  const title = (
    <>
      {texting ? <MessagingMark app={detail.thread.messaging_app} size={15} /> : null}
      <span className="truncate">{detail.thread.title}</span>
    </>
  );
  return (
    <h2 className="flex min-w-0 items-center gap-1.5 text-base leading-5 font-semibold text-foreground">
      {phone && onOpen ? (
        <button
          type="button"
          onClick={() => onOpen(true)}
          className="flex min-w-0 items-center gap-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {title}
        </button>
      ) : (
        title
      )}
    </h2>
  );
}

/**
 * Settle on a desktop (slice 4 §6.3), beside Rename: "Settled" once it is,
 * until a person writes again. A routine's own chat can't be settled; the
 * Worker says so. On a phone it's in Chat info.
 */
function SettleButton({ detail }: { detail: ThreadDetail }) {
  const [open, setOpen] = useState(false);
  if (detail.thread.settled) {
    return <span className="text-meta text-muted-foreground">Settled</span>;
  }
  return (
    <>
      <Button variant="ghost" size="sm" aria-label="Settle" onClick={() => setOpen(true)}>
        <CheckCheck />
        Settle
      </Button>
      <SettleWindow threadId={detail.thread.id} open={open} onOpenChange={setOpen} />
    </>
  );
}

/**
 * A started thread's controls: Rename on a desktop; on a phone "…", the
 * Area's tab and the Chat info they open (Paper HF).
 */
function TitleControls({
  detail,
  me,
  line,
  filable,
  info,
  onInfo,
  onRename,
}: {
  detail: ThreadDetail;
  me: Me;
  line: string | null;
  /** Not an email chat: it can have an Area. */
  filable: boolean;
  info: boolean;
  onInfo: (open: boolean) => void;
  onRename: () => void;
}) {
  const phone = useIsMobile();
  const tab = filable ? detail.thread.area : null;
  return (
    <>
      {phone ? null : <SettleButton detail={detail} />}
      <Button
        variant="ghost"
        size="sm"
        aria-label="Rename"
        onClick={onRename}
        className="hidden md:inline-flex"
      >
        <Pencil />
        Rename
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Chat info"
        onClick={() => onInfo(true)}
        // 44px to tap: the inset counts from inside the button's 1px border.
        className="relative after:absolute after:-inset-[9px] md:hidden"
      >
        <Ellipsis />
      </Button>
      {tab ? <AreaTab area={tab} onOpen={() => onInfo(true)} /> : null}
      {phone ? (
        <ChatInfo
          detail={detail}
          me={me}
          line={line}
          open={info}
          onOpenChange={onInfo}
          onRename={onRename}
        />
      ) : null}
    </>
  );
}

function ThreadHeader({ threadId, me }: { threadId: string; me: Me }) {
  const thread = useThread(threadId);
  const { search } = useLocation();
  const [editing, setEditing] = useState(false);
  const detail = thread.data;
  return (
    <header className="relative flex min-h-[52px] shrink-0 items-center gap-3 border-b border-border px-4 py-0.5 md:min-h-[68px] md:px-7 md:py-3">
      <Link
        href={`/chats${search}`}
        aria-label="All chats"
        className="-mr-2 -ml-3 flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground md:hidden"
      >
        <ChevronLeft className="size-6" />
      </Link>
      {detail ? (
        <TitleOrEditor
          detail={detail}
          starting={thread.isPlaceholderData}
          editing={editing}
          onEditing={setEditing}
          threadId={threadId}
          me={me}
        />
      ) : null}
    </header>
  );
}

/** The open thread's title, which anyone in the workspace can rename. */
function TitleOrEditor({
  detail,
  starting,
  editing,
  onEditing,
  threadId,
  me,
}: {
  detail: ThreadDetail;
  me: Me;
  /** Still being started (its placeholder): nothing to rename yet. */
  starting: boolean;
  editing: boolean;
  onEditing: (on: boolean) => void;
  threadId: string;
}) {
  if (!editing || starting) {
    return (
      <ThreadTitle
        detail={detail}
        me={me}
        onRename={starting ? undefined : () => onEditing(true)}
      />
    );
  }
  return (
    <TitleEditor threadId={threadId} title={detail.thread.title} onDone={() => onEditing(false)} />
  );
}

/**
 * The list and the open thread side by side; on a phone one at a time (the
 * thread has a back arrow), between the top bar and the tab bar.
 */
function ChatsLayout({ me, threadId }: { me: Me; threadId: string | undefined }) {
  const navigate = useNavigate();
  const [area, setArea] = useAreaFilter();
  const suffix = area ? `?area=${area}` : "";
  return (
    <div className={FULL_HEIGHT_PAGE}>
      <section
        aria-label="Threads"
        className={cn(
          "w-full flex-col md:flex md:w-[500px] md:max-w-[45%] md:shrink-0 md:border-r md:border-border",
          threadId ? "hidden" : "flex",
        )}
      >
        {/* Not on a phone: the tab bar already says Chats. */}
        <header className="hidden flex-col gap-3.5 px-6 pt-8 pb-3.5 md:flex">
          <div className="flex items-center justify-between gap-4">
            <PageTitle>Chats</PageTitle>
            <AreaFilter me={me} value={area} onChange={setArea} />
          </div>
        </header>
        {/* A phone's filter: chips above the list. */}
        <AreaFilter me={me} value={area} onChange={setArea} className="px-4 pt-3 pb-1 md:hidden" />
        <ThreadList me={me} currentId={threadId} area={area} />
        <div className="px-4 pt-3 pb-5">
          <StartComposer
            placeholder="Ask for something. It starts a new thread."
            label="Start a thread"
            surface="chats"
            // Slice 6d: a chat started from the list filtered to an Area is in it.
            areaId={filingArea(area, me)}
            onStarted={(id) => navigate(`/chats/${id}${suffix}`)}
          />
        </div>
      </section>

      <section
        aria-label="Thread"
        className={cn("min-w-0 flex-1 flex-col bg-card/35 md:flex", threadId ? "flex" : "hidden")}
      >
        {threadId ? (
          <Fragment key={threadId}>
            <ThreadHeader threadId={threadId} me={me} />
            <ThreadView
              threadId={threadId}
              me={me}
              onStartEdited={({ body, doc_id, files }) => {
                // The thread never existed: back to the list, its words (doc, files) in the composer.
                startDrafts.put("chats", body, doc_id, files);
                void navigate("/chats", { replace: true });
              }}
            />
          </Fragment>
        ) : (
          <div className="flex flex-1 items-center justify-center px-10 text-center text-body text-muted-foreground">
            Pick a thread, or ask for something to start one.
          </div>
        )}
      </section>
    </div>
  );
}

/** The route: the shell's guard has already resolved `/api/me`. */
export function ChatsPage() {
  const { data: me } = useMe();
  const { threadId } = useParams();
  return me ? <ChatsLayout me={me} threadId={threadId} /> : null;
}
